/**
 * pod-demand — groups a Solution's monthly hours (logged actuals behind, NOS guidance
 * ahead) by who carries them on the Pod: one group per Primary Position (R1, R2, …) and
 * one Supporting group that also absorbs anyone who logged or is forecast against the
 * Solution without being assigned to it.
 *
 * Pure and display-only. It reads the Solution Workload payload's per-Resource hours and
 * never derives points, Intensity or anything Fit Score reads (hours ≠ capacity).
 */

/** The minimal per-month shape this module needs from the Solution Workload payload. */
export interface DemandMonthInput {
  month: string;
  kind: "actual" | "guidance";
  actualsByResource: Array<{ resourceId: string; name: string; hours: number }>;
  guidanceByResource: Array<{ resourceId: string; name: string; hours: number }>;
}

/** "R1" … "R4" for a Primary Position, or the Supporting group. */
export type DemandGroupKey = string;
export const SUPPORTING_GROUP: DemandGroupKey = "Supporting";

export interface DemandContributor {
  resourceId: string;
  name: string;
  hours: number;
  /** True when the Resource is not assigned to the Solution at all (folded into Supporting). */
  offPod: boolean;
}

export interface PodDemandMonth {
  month: string;
  kind: "actual" | "guidance";
  /** Hours per group for the month. A group with no hours is absent. */
  byGroup: Map<DemandGroupKey, number>;
  /** Who made up each group's hours this month, hours-desc. */
  contributors: Map<DemandGroupKey, DemandContributor[]>;
  total: number;
}

/**
 * Bucket each month's per-Resource hours into Pod groups.
 *
 * @param groupByResource resourceId → its group: the Position it fills, or
 *   {@link SUPPORTING_GROUP} for a Supporting Assignment. Anyone missing from the map
 *   is off-Pod and lands in Supporting with `offPod: true`.
 */
export function groupDemandByPod(
  months: DemandMonthInput[],
  groupByResource: Map<string, DemandGroupKey>,
): PodDemandMonth[] {
  return months.map((m) => {
    const rows = m.kind === "actual" ? m.actualsByResource : m.guidanceByResource;
    const byGroup = new Map<DemandGroupKey, number>();
    const contributors = new Map<DemandGroupKey, DemandContributor[]>();
    let total = 0;
    for (const r of rows) {
      if (r.hours <= 0) continue;
      const assigned = groupByResource.get(r.resourceId);
      const group = assigned ?? SUPPORTING_GROUP;
      byGroup.set(group, (byGroup.get(group) ?? 0) + r.hours);
      const list = contributors.get(group) ?? [];
      list.push({
        resourceId: r.resourceId,
        name: r.name,
        hours: r.hours,
        offPod: assigned === undefined,
      });
      contributors.set(group, list);
      total += r.hours;
    }
    for (const list of contributors.values()) list.sort((a, b) => b.hours - a.hours);
    return { month: m.month, kind: m.kind, byGroup, contributors, total };
  });
}

export interface DemandSummary {
  /** Average logged hours per month across the actual months; null when there are none to show. */
  loggedPerMonth: number | null;
  /** Average NOS guidance hours per month across the guidance months; null when unavailable. */
  projectedPerMonth: number | null;
  /** One value per month for a sparkline; null where that side of the window has no data. */
  series: Array<{ month: string; kind: "actual" | "guidance"; hours: number | null }>;
}

/**
 * One Resource's logged vs projected hours on this Solution, for a Pod card.
 *
 * `actualsEmpty` (no linked Demands) and `guidanceUnavailable` (NOS unreachable) blank
 * their side rather than reading as zero — "no data" and "no hours" are different facts.
 */
export function summarizeResourceDemand(
  months: DemandMonthInput[],
  resourceId: string,
  opts: { actualsEmpty: boolean; guidanceUnavailable: boolean },
): DemandSummary {
  let loggedSum = 0;
  let loggedCount = 0;
  let projectedSum = 0;
  let projectedCount = 0;
  const series = months.map((m) => {
    const isActual = m.kind === "actual";
    const blank = isActual ? opts.actualsEmpty : opts.guidanceUnavailable;
    if (blank) return { month: m.month, kind: m.kind, hours: null };
    const rows = isActual ? m.actualsByResource : m.guidanceByResource;
    const hours = rows
      .filter((r) => r.resourceId === resourceId)
      .reduce((sum, r) => sum + r.hours, 0);
    if (isActual) {
      loggedSum += hours;
      loggedCount += 1;
    } else {
      projectedSum += hours;
      projectedCount += 1;
    }
    return { month: m.month, kind: m.kind, hours };
  });
  return {
    loggedPerMonth: loggedCount > 0 ? loggedSum / loggedCount : null,
    projectedPerMonth: projectedCount > 0 ? projectedSum / projectedCount : null,
    series,
  };
}

export type PodCoverage = "matches" | "slightly-over" | "well-over" | "slightly-short" | "short";

/**
 * Plain-words read of the Pod's points against the Solution's Intensity, replacing the
 * "5/4 pts · Overstaffed +1 pt" readout. Within a quarter of Intensity either way is
 * "slightly"; beyond that it is "well over" / "short".
 */
export function podCoverage(totalPoints: number, intensity: number): PodCoverage {
  if (totalPoints === intensity) return "matches";
  const ratio = intensity > 0 ? totalPoints / intensity : Number.POSITIVE_INFINITY;
  if (totalPoints > intensity) return ratio <= 1.25 ? "slightly-over" : "well-over";
  return ratio >= 0.75 ? "slightly-short" : "short";
}

export const POD_COVERAGE_LABEL: Record<PodCoverage, string> = {
  matches: "Pod matches this Intensity",
  "slightly-over": "Pod is a little over this Intensity",
  "well-over": "Pod is well over this Intensity",
  "slightly-short": "Pod is a little short of this Intensity",
  short: "Pod is short of this Intensity",
};
